import React, { useEffect, useRef, useState } from 'react';
import { Link, Navigate, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import classNames from 'classnames';
import { clearAuthError, loginEmail, resetState } from '@/actions';
import { AuthState } from '@/types';
import TelegramLoginButton from '@/components/TelegramLoginButton';
import StaticLayout from '@/layouts/StaticLayout';
import LoaderIcon from '@/images/icons/Loader';
import ModalAction from '@/components/ModalAction';
import TelegramIcon from '@/images/icons/TelegramIcon';
import EmailConfirmModal from '@/components/EmailConfirmModal';
import useSeamlessLogin from '@/hooks/useSeamlessLogin';
import ErrorModal from '@/components/ErrorModal';
import { getRedirectUrlWithParams } from '@/utils/utils';

/* Logic:

1. If non-seamless login AND already logged in, redirect to /
2. If non-seamless login AND not logged in, show Telegram Login Widget
3. If seamless login AND already logged in with SAME userId, redirect to /
4. If seamless login AND not logged in OR logged in with DIFFERENT userId, execute login and AFTER that redirect to /
*/

const Login: React.FC = () => {
  const {
    loginAttemptMade,
    isSeamlessLogin,
    preLoggedIn,
    onLogin,
    isLoginFailed,
  } = useSeamlessLogin();

  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const redirectedEmail = location.state?.email || '';

  const auth = useSelector((state: { auth: AuthState }) => state.auth);
  const [justLoggedOut, setJustLoggedOut] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [telegramModalOpen, setTelegramModalOpen] = useState<boolean>(false);
  const [emailConfirmModalOpen, setEmailConfirmModalOpen] = useState<boolean>(false);
  const [errorModal, setErrorModal] = useState<string | null>(null);
  const [errorModalButton, setErrorModalButton] = useState<any>(null);
  const [errorModalOpen, setErrorModalOpen] = useState<boolean>(false);
  const [errorLogin, setErrorLogin] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [isEmailVerified, setIsEmailVerified] = useState<boolean>(false);

  const [email, setEmail] = useState<string>(redirectedEmail);
  const [lastEmail, setLastEmail] = useState<string>('');
  const [emailConfirmCode, setEmailConfirmCode] = useState<string>('');
  const [requestId, setRequestId] = useState<string | null>(null);
  const requestIdRef = useRef<string | null>(null);
  const [resendTimer, setResendTimer] = useState<number>(0);
  const [canResend, setCanResend] = useState<boolean>(true);
  const [isEmailVerifyLoading, setIsEmailVerifyLoading] = useState<boolean>(false);
  const [hasResendBeenClicked, setHasResendBeenClicked] = useState<boolean>(false);

  const redirect = searchParams.get('redirectUrl');

  useEffect(() => {
    if (auth.justLoggedOut) {
      dispatch(resetState());
    }
    return () => {
      if (auth.error) {
        dispatch(clearAuthError());
      }
    }
  }, []);

  useEffect(() => {
    if (auth.user) {
      if (redirect) {
        navigate(redirect, { replace: true });
      }
    }
  }, [auth.user, navigate, searchParams]);

  useEffect(() => {
    if (auth.justLoggedOut) {
      setJustLoggedOut(true);
    }
  }, [auth.justLoggedOut]);

  useEffect(() => {
    if (errorModal) {
      setTelegramModalOpen(false);
      setEmailConfirmModalOpen(false);
      setErrorModalOpen(true);
    }
  }, [errorModal]);

  useEffect(() => {
    if (!errorModalOpen) {
      setErrorModal(null);
      dispatch(clearAuthError());
    }
  }, [errorModalOpen]);

  useEffect(() => {
    requestIdRef.current = requestId;
  }, [requestId]);

  useEffect(() => {
    if (auth?.error) {
      setErrorModal(auth.error);
      if (auth.errorStatus === 409) {
        setErrorModalButton('Please login via Telegram instead');
      }
    }
  }, [auth?.error]);

  useEffect(() => {
    if (email !== lastEmail) {
      setIsEmailVerified(false);
      setRequestId(null);
    }
  }, [email]);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (resendTimer > 0) {
      timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
    } else {
      setCanResend(true);
    }
    return () => clearTimeout(timer);
  }, [resendTimer]);

  useEffect(() => {
    if (emailConfirmCode.length === 6) {
      onEmailVerify();
    }
  }, [emailConfirmCode.length]);

  useEffect(() => {
    if (!emailConfirmModalOpen) {
      setErrorCode(null);
    }
  }, [emailConfirmModalOpen]);

  const sendCodeEmail = async () => {
    try {
      setIsLoading(true);
      setErrorModal(null);

      const response = await fetch(`${process.env.REACT_APP_API_URL}/email/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        if (data.redirectUrl) {
          navigate(`/signup?${searchParams}`, { state: { email } });
        } else if (data.requestId) {
          setRequestId(data.requestId);
          setErrorModal(null);
          setEmailConfirmModalOpen(true);
          setResendTimer(60);
          setCanResend(false);
        }
      } else {
        setErrorLogin(data.message);
      }
    } catch (error) {
      console.error('Error creating recovery request:', error);
      setErrorModal('An error occurred, please try again later.');
    } finally {
      setIsLoading(false);
    }
  }

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setErrorLogin(null);
    setLastEmail(email);
    if (isLoading) return;

    if (!requestId) {
      sendCodeEmail();
    } else if (requestId && !isEmailVerified) {
      setEmailConfirmModalOpen(true);
    } else if (requestId && isEmailVerified) {
      setTelegramModalOpen(true);
    }
  };

  const onEmailResend = () => {
    setHasResendBeenClicked(true);

    if (canResend) {
      setResendTimer(60);
      setCanResend(false);
      sendCodeEmail();
    }
  };

  const onEmailVerify = async () => {
    if (isEmailVerifyLoading) return;

    try {
      setErrorCode(null);
      setIsEmailVerifyLoading(true);
      setErrorModal(null);

      const response = await fetch(`${process.env.REACT_APP_API_URL}/email/login-verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          requestId,
          verificationCode: emailConfirmCode,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setErrorModal(null);
        setIsEmailVerified(true);
        dispatch(loginEmail(data));
      } else if (data.toTelegramConnect) {
        setErrorModal(null);
        setIsEmailVerified(true);
        setEmailConfirmModalOpen(false)
        setTelegramModalOpen(true);
      } else {
        setErrorCode(data.message);
      }
    } catch (error: any) {
      console.error('Error verifying code:', error);
      setErrorModal(error?.response?.message || 'An error occurred, please try again later.');
    } finally {
      setIsEmailVerifyLoading(false);

    }
  };

  if (preLoggedIn) {
    const redirectUrl = getRedirectUrlWithParams(searchParams);
    return <Navigate to={redirectUrl} replace />;
  }

  if (isSeamlessLogin && !loginAttemptMade) {
    return null;
  }

  const footerLinks = [
    {
      title: 'Terms',
      link: 'https://www.invitemember.com/terms',
    },
    {
      title: 'Privacy',
      link: 'https://www.invitemember.com/privacy'
    },
    {
      title: 'Support',
      link: 'https://t.me/InviteMemberSupportBot'
    }
  ];

  return (
    <StaticLayout logoPosition={"left"} size={'xl'} layoutCenter layoutFixed>
      <div className="basis-0 grow shrink my-10 md:my-0">
        {(loginAttemptMade && !preLoggedIn && !isLoginFailed) ? (
          <div className="flex items-center justify-center py-1">
            <svg
              className="animate-rotation w-10 h-10"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2"
                stroke="white"
                strokeWidth="3"
                strokeLinecap="round"
              />
              <path
                d="M12 2C11.4467 2 10.9039 2.04494 10.375 2.13136"
                stroke="white"
                strokeWidth="3"
                strokeLinecap="round"
              />
              <path
                d="M2 12C2 17.5228 6.47715 22 12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2"
                stroke="#8A9698"
                strokeWidth="3"
                strokeLinecap="round"
              />
            </svg>
          </div>
        ) : (
          <>
            {justLoggedOut ? (
              <div className="text-center">
                <div className="text-3xl md:text-[5.5rem] font-semibold text-brand-green mt-2 mb-[22px] md:mb-8 font-founders tracking-normal leading-none md:text-center max-w-[422px] md:max-w-[35rem] mx-auto">
                  Almost Logged Out
                </div>
                <div className="h1 mt-2 mb-[22px] md:mb-8 font-founders tracking-normal leading-none md:text-center max-w-[422px] mx-auto">
                  For a complete logout, please clear Cookies in this browser.
                </div>
                <div>
                  <span className="text-[#A5A5A5]">You can also return to the app</span>
                  &nbsp;
                  <button onClick={() => {
                    window.location.reload();
                  }} className="text-[#3CBBCE]">Log in</button>
                </div>
              </div>
            ) : (
              <h1 className="text-3xl md:text-[5.5rem] font-semibold text-brand-green mb-5 md:mb-14 font-founders tracking-normal leading-none text-center mx-auto">
                Welcome back to InviteMember
              </h1>
            )}

            {!justLoggedOut && (
              <div className="max-w-[422px] mx-auto">
                <form onSubmit={onSubmit}>
                  <input
                    type="email"
                    className="h-10 md:h-16 w-full py-2 md:py-[18px] px-4 md:px-5 text-base bg-[#FEFEF9] border border-[#DBDBDB] rounded-[8px] md:rounded-[12px] placeholder:text-[#A5A5A5] focus:border-[#3CBBCE] transition"
                    placeholder="Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                  {errorLogin && (
                    <div className="text-sm text-red-orange mt-2 font-medium">
                      {errorLogin}
                    </div>
                  )}
                  <button
                    className={classNames(`btn-primary w-full h-10 md:h-14 mt-4 md:mt-6 mb-2`)}
                  >
                    {isLoading ? (
                      <div className="flex items-center justify-center py-1 pointer-events-none">
                        <LoaderIcon className="animate-rotation w-6 h-6 text-white" />
                      </div>
                    ) : (
                      <span className="font-founders tracking-[0.02em] text-2xl md:text-3xl/[0.8] font-semibold translate-y-[-1px] md:translate-y-[-3px]">
                        Continue
                      </span>
                    )}
                  </button>
                </form>

                <div className="relative flex pt-4 md:pt-8 pb-6 md:pb-8 items-center text-center">
                  <div className="flex-grow border-t border-[#DBDBDB]"></div>
                  <span className="flex-shrink mx-4 text-[#A5A5A5] text-[13px] md:text-base">or</span>
                  <div className="flex-grow border-t border-[#DBDBDB]"></div>
                </div>

                <div>
                  <button
                    className="flex items-center justify-center gap-[12%] border border-[#54A9EA] bg-[#54A9EA] text-white w-full h-10 md:h-14 rounded-full text-[13px] md:text-base"
                    onClick={() => setTelegramModalOpen(true)}
                  >
                    <svg
                      width="27"
                      height="23"
                      viewBox="0 0 27 23"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M1.85611 9.63259C9.10385 6.47486 13.9368 4.3931 16.355 3.38729C23.2594 0.515507 24.6941 0.0166483 25.6292 0.000175635C25.8349 -0.00344734 26.2947 0.047523 26.5926 0.289228C26.8441 0.49332 26.9133 0.769018 26.9464 0.962519C26.9795 1.15602 27.0208 1.59682 26.988 1.94125C26.6138 5.8725 24.9949 15.4126 24.1713 19.8157C23.8228 21.6788 23.1365 22.3035 22.4722 22.3646C21.0284 22.4975 19.9321 21.4105 18.5337 20.4938C16.3455 19.0594 15.1094 18.1665 12.9854 16.7669C10.5307 15.1493 12.122 14.2602 13.5209 12.8073C13.887 12.427 20.2483 6.64093 20.3714 6.11604C20.3868 6.05039 20.4011 5.80569 20.2557 5.67648C20.1103 5.54728 19.8958 5.59146 19.741 5.6266C19.5215 5.67641 16.0259 7.98689 9.25407 12.558C8.26184 13.2394 7.36312 13.5713 6.55789 13.554C5.6702 13.5348 3.96262 13.052 2.69322 12.6394C1.13624 12.1333 -0.101214 11.8657 0.006541 11.0062C0.0626662 10.5585 0.679189 10.1006 1.85611 9.63259Z"
                        fill="white"
                      />
                    </svg>
                    <span>Log in with Telegram</span>
                  </button>
                </div>
                <div className="mt-20 text-center text-[13px] md:text-base">
                  <span className="text-[#A5A5A5]">Don't have an account?</span>
                  &nbsp;
                  {redirect ? (
                    <Link to={`/signup?redirectUrl=${encodeURIComponent(redirect)}`} className="text-[#3CBBCE]">Sign up</Link>
                  ) : (
                    <Link to="/signup" className="text-[#3CBBCE]">Sign up</Link>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <ModalAction
        id="login-telegram-modal"
        modalOpen={telegramModalOpen}
        setModalOpen={setTelegramModalOpen}
        size={undefined}
      >
        <TelegramIcon className="w-14 h-14 rounded-full mb-6" />
        <h2 className="h2 mb-6 leading-[0.9]">
          Please log in using your Telegram account.
        </h2>
        {auth?.isLoggingIn ? (
          <div className="w-10 h-10 flex items-center justify-center mx-auto overflow-hidden">
            <svg className="w-8 h-8 animate-rotation"
                 width="48"
                 height="48"
                 viewBox="0 0 48 48"
                 fill="none"
                 xmlns="http://www.w3.org/2000/svg">
              <path d="M44 24C44 35.0457 35.0457 44 24 44C12.9543 44 4 35.0457 4 24C4 12.9543 12.9543 4 24 4"
                    stroke="#EAE6E0"
                    strokeWidth="8"
                    strokeLinecap="round" />
              <path d="M24 4C22.8934 4 21.8077 4.08988 20.75 4.26273"
                    stroke="#EAE6E0"
                    strokeWidth="8"
                    strokeLinecap="round" />
              <path d="M4 24C4 35.0457 12.9543 44 24 44C35.0457 44 44 35.0457 44 24C44 12.9543 35.0457 4 24 4"
                    stroke="#0D4E58"
                    strokeWidth="8"
                    strokeLinecap="round" />
            </svg>
          </div>
        ) : (
          <TelegramLoginButton
            dataOnauth={(data: any) => onLogin(data, false, requestIdRef.current)}
            botName={process.env.REACT_APP_BOT_USERNAME}
          />
        )}
      </ModalAction>

      <EmailConfirmModal
        modalOpen={emailConfirmModalOpen}
        setModalOpen={setEmailConfirmModalOpen}
        email={email}
        emailConfirmCode={emailConfirmCode}
        setEmailConfirmCode={setEmailConfirmCode}
        resendTimer={resendTimer}
        onResend={onEmailResend}
        onVerify={onEmailVerify}
        hasResendBeenClicked={hasResendBeenClicked}
        isLoading={isEmailVerifyLoading || auth?.isLoggingIn}
        error={errorCode}
      />
      <ErrorModal
        modalOpen={errorModalOpen}
        setModalOpen={setErrorModalOpen}
        error={errorModal}
        buttonText={errorModalButton}
      />
      <div className="flex items-center justify-center mt-auto md:mt-[60px] mb-3 flex-row">
        {footerLinks.map((item) => (
          <a key={item.link}
             href={item.link}
             className="text-[11px]/[1.4] md:text-[13px] text-brand-green relative before:first:hidden before:content-[''] before:mr-2 before:ml-0 before:block before:w-[3px] md:before:w-1 before:h-[3px] md:before:h-1 before:bg-brand-green before:rounded-full before:absolute before:z-[1] before:left-0 before:top-1/2 before:-translate-y-1/2 before:-translate-x-1/2 before:opacity-50 before:pointer-events-none"
          >
            <span className="px-[14px] py-2 opacity-50 hover:opacity-100 transition">{item.title}</span>
          </a>
        ))}
      </div>
    </StaticLayout>
  );
};

export default Login;
